/**
 * Bulk Operations Store
 *
 * Manages state for bulk operations (lockdown, unlock) across multiple devices.
 * Replaces module-level variables for tracking bulk operation progress.
 *
 * Phase 3D PR #1: Module State Migration
 */

import { create } from "zustand";
import logger from "../utils/logger";

export const useBulkOperationsStore = create((set, get) => ({
  // --- Bulk Lockdown Tracking ---
  // Track which devices receive which updates during bulk lockdown operations
  bulkLockdownTracking: {},
  lastLockdownEventTime: 0,

  // --- Bulk Device Lockdown States ---
  // Map of deviceId -> lockdownState (synced from deviceStates)
  bulkDeviceLockdownStates: {},

  /**
   * Update tracking for a specific device
   * @param {string} deviceId - Device ID
   * @param {object} updates - Updates to track (lockdownState, lockState, etc.)
   */
  updateDeviceTracking: (deviceId, updates) => {
    set((state) => ({
      bulkLockdownTracking: {
        ...state.bulkLockdownTracking,
        [deviceId]: {
          ...(state.bulkLockdownTracking[deviceId] || {}),
          ...updates,
        },
      },
      lastLockdownEventTime: Date.now(),
    }));
  },

  /**
   * Reset bulk lockdown tracking (logs summary before clearing)
   */
  resetBulkLockdownTracking: () => {
    const { bulkLockdownTracking } = get();

    if (Object.keys(bulkLockdownTracking).length > 0) {
      logger.log(
        `[BulkLockdown] 📊 SUMMARY for ${
          Object.keys(bulkLockdownTracking).length
        } devices:`
      );
      Object.entries(bulkLockdownTracking).forEach(([deviceId, updates]) => {
        logger.log(`[BulkLockdown]   Device ${deviceId}:`, {
          lastActionUpdated: updates.lastActionUpdated || "NO",
          lockdownState: updates.lockdownState || "NO",
          lockState: updates.lockState || "NO",
          actionStatus: updates.actionStatus || "N/A",
        });
      });
    }

    set({ bulkLockdownTracking: {} });
  },

  /**
   * Check if tracking should be reset (after 10 seconds of no events)
   */
  checkResetBulkLockdownTracking: () => {
    const {
      lastLockdownEventTime,
      bulkLockdownTracking,
      resetBulkLockdownTracking,
    } = get();
    const now = Date.now();

    if (
      now - lastLockdownEventTime > 10000 &&
      Object.keys(bulkLockdownTracking).length > 0
    ) {
      resetBulkLockdownTracking();
    }
  },

  /**
   * Set bulk device lockdown states (synced from deviceStates)
   * @param {object|function} statesOrUpdater - New states object or updater function
   */
  setBulkDeviceLockdownStates: (statesOrUpdater) => {
    set((state) => ({
      bulkDeviceLockdownStates:
        typeof statesOrUpdater === "function"
          ? statesOrUpdater(state.bulkDeviceLockdownStates)
          : statesOrUpdater,
    }));
  },

  /**
   * Set the last lockdown event time (used by external callers)
   */
  setLastLockdownEventTime: (time) => {
    set({ lastLockdownEventTime: time });
  },

  // --- Device State Update Tracking ---
  // Track consecutive deviceStateUpdate events for debugging
  deviceStateUpdateCounter: 0,
  lastDeviceStateUpdateTime: 0,

  /**
   * Increment device state update counter
   */
  incrementDeviceStateUpdateCounter: () => {
    set((state) => ({
      deviceStateUpdateCounter: state.deviceStateUpdateCounter + 1,
      lastDeviceStateUpdateTime: Date.now(),
    }));
  },

  /**
   * Reset device state update tracking (logs summary before clearing)
   */
  resetDeviceStateUpdateTracking: () => {
    const { deviceStateUpdateCounter } = get();

    if (deviceStateUpdateCounter > 0) {
      logger.log(
        `[DeviceStateUpdate] 🔚 Sequence complete: ${deviceStateUpdateCounter} events received`
      );
    }

    set({
      deviceStateUpdateCounter: 0,
      lastDeviceStateUpdateTime: Date.now(),
    });
  },

  /**
   * Check if device state update tracking should be reset (after 5 seconds of no events)
   */
  checkResetDeviceStateUpdateTracking: () => {
    const {
      lastDeviceStateUpdateTime,
      deviceStateUpdateCounter,
      resetDeviceStateUpdateTracking,
    } = get();
    const now = Date.now();

    if (
      now - lastDeviceStateUpdateTime > 5000 &&
      deviceStateUpdateCounter > 0
    ) {
      resetDeviceStateUpdateTracking();
    }
  },
}));
